import {
  ADMIN_BRAND_DETAILS,
  ADMIN_LOCATIONS_DETAILS,
  ADMIN_ORDER_DETAILS,
  ADMIN_USERS_DETAILS,
  DetailRow,
  Img,
  noImage,
  renderProviderIcon,
  renderProviderName,
  Separator,
  SystemProblem,
  useSafeWindowOpen,
} from '@vgrubs/components';
import { problemFacts } from '../helpers/systemProblemFacts';
import { problemBrandName } from '../helpers/systemProblemLabels';
import { PhoneLink } from './ProblemDetails.styled';

type Props = {
  problem: SystemProblem;
  /** The restaurant's phone, for the types where support should call. */
  phone?: string | null;
};

const ProviderLogo = ({ provider }: { provider: string }) => (
  <Img
    alt=""
    src={renderProviderIcon(provider, noImage)}
    $width="32px"
    $height="32px"
    $borderRadius="50%"
  />
);

/**
 * Everything a problem other than a duplicate knows, one DetailRow per fact:
 * what its `meta` carries (provider, menu, error, when...), then the order,
 * location and brand it is about (no brand for the location's own) - each opening its admin page in a new tab,
 * the problem being what support is working from - and who it concerns.
 */
export const ProblemDetails = ({ problem, phone }: Props) => {
  const openInNewTab = useSafeWindowOpen();
  const open = (route: string, id: number) =>
    openInNewTab(route.replace(':id', String(id)), { immediate: true });

  const { orderId, locationId, brandId, relatedUserId } = problem;
  const brandName = problemBrandName(problem);

  return (
    <>
      {problemFacts(problem).map((fact) => (
        <div key={fact.label}>
          <Separator $m="16px 0" />
          <DetailRow
            label={fact.label}
            hint={fact.hint}
            right={fact.provider && <ProviderLogo provider={fact.provider} />}
          >
            {fact.value}
          </DetailRow>
        </div>
      ))}

      {orderId !== null && (
        <>
          <Separator $m="16px 0" />
          <DetailRow
            label="Order"
            hint={
              problem.orderProvider && renderProviderName(problem.orderProvider)
            }
            onOpen={() => open(ADMIN_ORDER_DETAILS, orderId)}
          >
            {`#${problem.orderNumber ?? orderId}`}
          </DetailRow>
        </>
      )}

      {problem.locationName && (
        <>
          <Separator $m="16px 0" />
          <DetailRow
            label="Location"
            hint={
              phone && (
                <PhoneLink href={`tel:${phone.replace(/[^\d+]/g, '')}`}>
                  {phone}
                </PhoneLink>
              )
            }
            onOpen={
              locationId === null
                ? undefined
                : () => open(ADMIN_LOCATIONS_DETAILS, locationId)
            }
          >
            {problem.locationName}
          </DetailRow>
        </>
      )}

      {brandName && (
        <>
          <Separator $m="16px 0" />
          <DetailRow
            label="Brand"
            onOpen={
              brandId === null
                ? undefined
                : () => open(ADMIN_BRAND_DETAILS, brandId)
            }
          >
            {brandName}
          </DetailRow>
        </>
      )}

      {problem.relatedUserName && (
        <>
          <Separator $m="16px 0" />
          <DetailRow
            label="Concerns"
            onOpen={
              relatedUserId === null
                ? undefined
                : () => open(ADMIN_USERS_DETAILS, relatedUserId)
            }
          >
            {problem.relatedUserName}
          </DetailRow>
        </>
      )}
    </>
  );
};
